// Shared sleep preprocessing for forecast and stats:
// normalize -> merge overlaps -> classify nap/night -> group into "sleep days".

import {
  addDays,
  localDate,
  localMinutesOfDay,
  MS_PER_MINUTE,
  parseHHmm,
  toMs,
  type Instant,
} from './tz.js';
import { getAgeNorms } from './wake-windows.js';

/** Segments closer than this are merged (duplicates, double taps, overlapping entries). */
export const MERGE_GAP_MIN = 1;
/** An ongoing sleep older than this is treated as a forgotten timer and capped. */
export const MAX_ONGOING_SLEEP_MIN = 16 * 60;
/** Same as sanity.ts MAX_FINISHED_SLEEP_MIN (kept here to avoid an import cycle). */
const MAX_FINISHED_SLEEP_MS = 24 * 60 * 60_000;

export interface SleepLike {
  startAt: Instant;
  endAt: Instant | null;
}

export interface SleepSegment {
  start: number; // epoch ms
  end: number; // epoch ms (== now for ongoing)
  ongoing: boolean;
  /** Ongoing entry exceeded MAX_ONGOING_SLEEP_MIN and was capped. */
  stale: boolean;
}

export type SleepKind = 'nap' | 'night';

export interface ClassifiedSegment extends SleepSegment {
  kind: SleepKind;
  /** Local date on which the night started (night of D), null for naps. */
  nightOf: string | null;
}

export const durationMin = (s: { start: number; end: number }): number => (s.end - s.start) / MS_PER_MINUTE;

/** Parse, drop invalid/future, cap stale ongoing, sort and merge overlaps. */
export function normalizeSleeps(sleeps: readonly SleepLike[], now: Instant): SleepSegment[] {
  const nowMs = toMs(now);
  const raw: SleepSegment[] = [];
  for (const s of sleeps) {
    let start: number;
    let end: number | null;
    try {
      start = toMs(s.startAt);
      end = s.endAt === null ? null : toMs(s.endAt);
    } catch {
      continue; // unparseable timestamps are ignored
    }
    if (start > nowMs) continue;
    let ongoing = end === null;
    let stale = false;
    let e = end === null ? nowMs : Math.min(end, nowMs);
    if (ongoing && (nowMs - start) / MS_PER_MINUTE > MAX_ONGOING_SLEEP_MIN) {
      e = start + MAX_ONGOING_SLEEP_MIN * MS_PER_MINUTE;
      ongoing = false;
      stale = true;
    }
    if (e < start || (e === start && !ongoing)) continue;
    // a finished sleep longer than a day is a timer stopped days late: no real sleep to count (sanity.ts)
    if (!ongoing && !stale && e - start > MAX_FINISHED_SLEEP_MS) continue;
    raw.push({ start, end: e, ongoing, stale });
  }
  raw.sort((a, b) => a.start - b.start || a.end - b.end);
  const merged: SleepSegment[] = [];
  for (const s of raw) {
    const cur = merged[merged.length - 1];
    if (cur && s.start <= cur.end + MERGE_GAP_MIN * MS_PER_MINUTE) {
      if (s.end > cur.end || (s.end === cur.end && s.ongoing)) {
        cur.end = s.end;
        cur.ongoing = s.ongoing;
      }
      cur.stale = cur.stale || s.stale;
    } else {
      merged.push({ ...s });
    }
  }
  return merged;
}

export interface ClassifyOptions {
  tz: string;
  /** Sleep starting at/after this local minute is night sleep. */
  nightStartMin: number;
  /** Sleep starting before this local minute (early morning) belongs to the previous night. */
  morningCutoffMin: number;
  /** A morning sleep that starts within this many minutes after a night segment continues the night. */
  resettleGapMin: number;
  /** Reclassify a short early-evening first segment followed by a long gap as a catnap (off for young babies). */
  catnapRule: boolean;
}

/** Long sleep starting after this local minute counts as night even if before nightStart (early bedtime). */
export const LONG_EVENING_SLEEP_FROM_MIN = 15 * 60 + 30;
export const LONG_EVENING_SLEEP_MIN_DURATION = 240;
export const CATNAP_MAX_MIN = 90;
export const CATNAP_GAP_MIN = 45;
export const DEFAULT_NIGHT_START = '18:00';
export const DEFAULT_MORNING_CUTOFF = '06:00';

/** Classification options derived from the age table (and parent target bedtime, if any). */
export function defaultClassifyOptions(
  tz: string,
  ageDays?: number,
  settings?: { targetBedtime?: string | undefined },
): ClassifyOptions {
  let nightStart = parseHHmm(DEFAULT_NIGHT_START);
  if (ageDays !== undefined) {
    let earliest = getAgeNorms(ageDays).bedtimeMin[0];
    if (settings?.targetBedtime) earliest = Math.min(earliest, parseHHmm(settings.targetBedtime));
    nightStart = Math.min(20 * 60, Math.max(16 * 60 + 30, earliest - 60));
  }
  return {
    tz,
    nightStartMin: nightStart,
    morningCutoffMin: parseHHmm(DEFAULT_MORNING_CUTOFF),
    resettleGapMin: 20,
    catnapRule: ageDays === undefined || ageDays >= 12 * 7,
  };
}

export function classifySleeps(segments: readonly SleepSegment[], opts: ClassifyOptions): ClassifiedSegment[] {
  const out: ClassifiedSegment[] = [];
  for (const s of segments) {
    const startMin = localMinutesOfDay(s.start, opts.tz);
    const startDate = localDate(s.start, opts.tz);
    const dur = durationMin(s);
    let nightOf: string | null = null;
    if (startMin >= opts.nightStartMin) nightOf = startDate;
    else if (startMin < opts.morningCutoffMin) nightOf = addDays(startDate, -1);
    else if (startMin >= LONG_EVENING_SLEEP_FROM_MIN && dur >= LONG_EVENING_SLEEP_MIN_DURATION) nightOf = startDate;
    else {
      const prev = out[out.length - 1];
      if (
        prev?.kind === 'night' &&
        startMin < 10 * 60 &&
        (s.start - prev.end) / MS_PER_MINUTE <= opts.resettleGapMin
      ) {
        nightOf = prev.nightOf;
      }
    }
    out.push({ ...s, kind: nightOf ? 'night' : 'nap', nightOf });
  }
  if (opts.catnapRule) applyCatnapRule(out, opts);
  return out;
}

function applyCatnapRule(segs: ClassifiedSegment[], opts: ClassifyOptions): void {
  for (let i = 0; i < segs.length - 1; i++) {
    const s = segs[i];
    const next = segs[i + 1];
    const prev = segs[i - 1];
    if (!s || !next || s.kind !== 'night') continue;
    const isFirstOfNight = !prev || prev.nightOf !== s.nightOf;
    if (!isFirstOfNight || next.nightOf !== s.nightOf) continue;
    const startMin = localMinutesOfDay(s.start, opts.tz);
    const beforeBedtimeWindow = startMin >= opts.nightStartMin && startMin < opts.nightStartMin + 60;
    if (
      beforeBedtimeWindow &&
      durationMin(s) < CATNAP_MAX_MIN &&
      (next.start - s.end) / MS_PER_MINUTE >= CATNAP_GAP_MIN
    ) {
      s.kind = 'nap';
      s.nightOf = null;
    }
  }
}

export interface SleepDay {
  date: string;
  /** End of the night that started the day before (null if unknown or still asleep). */
  morningWake: number | null;
  /** Naps whose local start date is this date. */
  naps: ClassifiedSegment[];
  /** Night that started on this date (may cross midnight). */
  night: ClassifiedSegment[];
  /** Start of the first night segment. */
  bedtime: number | null;
}

/** Group classified segments into sleep days keyed by local date. */
export function buildSleepDays(segs: readonly ClassifiedSegment[], tz: string): Map<string, SleepDay> {
  const days = new Map<string, SleepDay>();
  const get = (date: string): SleepDay => {
    let d = days.get(date);
    if (!d) {
      d = { date, morningWake: null, naps: [], night: [], bedtime: null };
      days.set(date, d);
    }
    return d;
  };
  for (const s of segs) {
    if (s.kind === 'nap') get(localDate(s.start, tz)).naps.push(s);
    else if (s.nightOf) get(s.nightOf).night.push(s);
  }
  for (const d of [...days.values()]) {
    const first = d.night[0];
    const last = d.night[d.night.length - 1];
    if (first) d.bedtime = first.start;
    // Only a night that lasted past midnight gives a morning wake (an evening-only segment means the rest was not logged).
    if (last && !last.ongoing && localDate(last.end, tz) !== d.date) get(addDays(d.date, 1)).morningWake = last.end;
  }
  return days;
}

/** Night wakings = awake gaps between consecutive segments of one night. */
export function nightWakings(night: readonly ClassifiedSegment[]): { count: number; awakeMin: number } {
  let count = 0;
  let awake = 0;
  for (let i = 1; i < night.length; i++) {
    const a = night[i - 1];
    const b = night[i];
    if (!a || !b) continue;
    count++;
    awake += (b.start - a.end) / MS_PER_MINUTE;
  }
  return { count, awakeMin: awake };
}
